import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  ISoccerMatch,
  ISoccerMatchPreview,
  ISoccerMatchPreviewTeamForm,
} from "../../../types";
import { getMatchPreview } from "../api";
import { getTeamInfo, getTeamInfoByCode } from "./useTeamLogos";

export type Result = "W" | "L" | "D";

export interface ITeamForm {
  name: string;
  flag: string;
  results: Result[];
  goalsPerMatch: string;
}

export interface IH2HMatch {
  winnerName: string;
  winnerFlag: string;
  homeScore: number;
  awayScore: number;
  isDraw: boolean;
  date: string;
}

export interface IPreMatchStats {
  homeTeam: ITeamForm;
  awayTeam: ITeamForm;
  h2hMatches: IH2HMatch[];
}

function parseLastSix(lastSix: string): Result[] {
  return lastSix
    .split("")
    .map((c) => c.toUpperCase() as Result)
    .filter((c) => c === "W" || c === "L" || c === "D");
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function buildTeamForm(
  formData: ISoccerMatchPreviewTeamForm | undefined,
  teamName: string,
  teamCode?: string
): ITeamForm {
  const teamInfo = (teamCode && getTeamInfoByCode(teamCode)) || getTeamInfo(teamName);
  if (!formData) {
    return {
      name: teamInfo.translatedName || teamName,
      flag: teamInfo.imgSrc,
      results: [],
      goalsPerMatch: "0",
    };
  }

  const goalsPerMatch =
    formData.matchesPlayed > 0
      ? (formData.goalsFor / formData.matchesPlayed).toFixed(1)
      : "0";

  return {
    name: teamInfo.translatedName || teamName,
    flag: teamInfo.imgSrc,
    results: parseLastSix(formData.lastSix),
    goalsPerMatch,
  };
}

function buildH2H(
  preview: ISoccerMatchPreview | undefined,
  homeContestantName: string,
  awayContestantName: string
): IH2HMatch[] {
  // Use any-comp H2H for richer data, fallback to same-comp
  const h2h =
    preview?.previousMeetingsAnyComp?.matches?.length
      ? preview.previousMeetingsAnyComp
      : preview?.previousMeetings;

  if (!h2h?.matches?.length) return [];

  return h2h.matches.map((m) => {
    const isDraw = m.homeScore === m.awayScore;
    let winnerName = "";
    let winnerFlag = "";

    if (!isDraw) {
      const isHomeWinner = m.homeScore > m.awayScore;
      const winnerCode = isHomeWinner ? m.homeTeamCode : m.awayTeamCode;
      const winnerTeamName = isHomeWinner ? m.homeTeamName : m.awayTeamName;
      const winnerInfo = (winnerCode && getTeamInfoByCode(winnerCode)) || getTeamInfo(winnerTeamName);
      winnerName = winnerInfo.translatedName || winnerTeamName;
      winnerFlag = winnerInfo.imgSrc;
    }

    return {
      winnerName: isDraw ? "Draw" : winnerName,
      winnerFlag: isDraw ? "" : winnerFlag,
      homeScore: m.homeScore,
      awayScore: m.awayScore,
      isDraw,
      date: m.date ? formatDate(m.date) : "",
    };
  });
}

export function usePreMatchStats(soccerMatch: ISoccerMatch): IPreMatchStats {
  const homeContestant = soccerMatch.contestants[0];
  const awayContestant = soccerMatch.contestants[1];
  const matchId = soccerMatch.id;

  const { data } = useQuery({
    queryKey: ["match-preview", matchId],
    queryFn: () => getMatchPreview(matchId),
    staleTime: 24 * 60 * 60 * 1000, // 24h — preview data is static
    enabled: !!matchId,
    retry: false,
  });

  return useMemo(() => {
    const homeTeam = buildTeamForm(
      data?.form?.find((f) => f.contestantId === homeContestant?.id),
      homeContestant?.name ?? "",
      homeContestant?.code
    );
    const awayTeam = buildTeamForm(
      data?.form?.find((f) => f.contestantId === awayContestant?.id),
      awayContestant?.name ?? "",
      awayContestant?.code
    );
    const h2hMatches = buildH2H(
      data,
      homeContestant?.name ?? "",
      awayContestant?.name ?? ""
    );
    return { homeTeam, awayTeam, h2hMatches };
  }, [data, homeContestant?.id, homeContestant?.name, awayContestant?.id, awayContestant?.name]);
}
